// Popups
//
// uses:
// - Alpine.js
//
// usage:
// - attach to a <dialog> element
//    eg: <dialog id="popup" x-data="popup()">
//
// - to open the modal, call open() from a trigger.
//    eg: Alpine.$data(document.querySelector('#popup')).open()
//
// - to close the modal, bind a close button
//    eg: <button x-bind="closeButtonEl"> Close </button>
//    eg: Alpine.$data(document.querySelector('#popup')).close()
//
// - to animate height changes caused by content (eg: content loaded async),
//   bind the element whose height should be tracked, and use the resulting
//   CSS variable for the height of the dialog, along with a height transition
//    eg: <dialog x-data="popup()" x-bind="dialogEl"
//          class="h-[var(--popup-content-height,fit-content)] transition-all">
//          <div x-bind="contentEl"> … </div>
//        </dialog>

(function () {
  document.addEventListener('alpine:init', () => {
    Alpine.data('popup', () => ({
      // state + api
      isActive: false,
      contentHeight: null,
      isResizing: false,
      open() {
        this.isActive = true;
      },
      close() {
        this.isActive = false;
      },

      // alpine directives
      init() {
        this.$watch('isActive', (value) => {
          if (value) {
            this.$el.showModal();
            if ('bodyScrollLock' in window) {
              window.bodyScrollLock.disableBodyScroll(this.$el);
            }
          } else {
            this.$el.close();
          }
        });
      },

      // track the height of the dialog's content, eg: as content loads
      observeContentHeight(content) {
        if (!('ResizeObserver' in window)) {
          return;
        }
        new ResizeObserver(([entry]) => {
          // unrounded and unaffected by transforms (eg: a scale while opening)
          const height = entry.borderBoxSize[0].blockSize;
          // skip while closed (the content then has no height)
          if (height) {
            this.contentHeight = height;
          }
        }).observe(content);
      },

      // 1. the <dialog> root elements
      dialogEl: {
        ['x-on:close']() {
          if ('bodyScrollLock' in window) {
            window.bodyScrollLock.enableBodyScroll(this.$el);
          }
          this.isActive = false;
        },
        ['x-trap.inert.noscroll']: 'isActive',

        // expose the content's height to CSS
        [':style']() {
          return this.contentHeight
            ? { '--popup-content-height': this.contentHeight + 'px' }
            : {};
        },

        // hide scrollbars while the height catches up with the content
        [':class']() {
          return { 'overflow-hidden': this.isResizing };
        },
        ['x-on:transitionrun.self'](e) {
          if (e.propertyName === 'height') this.isResizing = true;
        },
        ['x-on:transitionend.self'](e) {
          if (e.propertyName === 'height') this.isResizing = false;
        },
        ['x-on:transitioncancel.self'](e) {
          if (e.propertyName === 'height') this.isResizing = false;
        },
      },

      // 2. the content element, whose height the dialog follows
      contentEl: {
        ['x-init']() {
          this.observeContentHeight(this.$el);
        },
      },

      // 3. the close <button> element
      closeButtonEl: {
        ['x-on:click']() {
          this.close();
        },
      },
    }));
  });
})();
